import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'
import { Icon } from 'components/ui/Icon'
import { messagesSelectors } from 'store/messages'
import { settingsSelectors } from 'store/settings'
import { useModalActions } from 'providers/ModalProvider'
import { useDetectClickOutside } from 'hooks/useDetectClickOutside'
import { useChatPageShell } from 'components/chat-page/ChatPageShell'

export const ChatPageHeader = () => {
  const { t } = useTranslation()
  const { toggleMobileSidebar, toggleCollapsed } = useChatPageShell()
  const currentChat = useSelector(messagesSelectors.currentChat)
  const isInOperatorMode = useSelector(messagesSelectors.currentChatIsInOperatorMode)
  const isLogged = useSelector(settingsSelectors.isLogged)
  const { showExportChat } = useModalActions()
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useDetectClickOutside({ setShowModal: setShowDropdown })

  const dropdownMenu = useMemo(() => {
    return _.compact([
      isLogged && {
        title: t('exportChat'),
        icon: <Icon icon={['far', 'file-export']} className="tw-h-4 tw-w-4" />,
        callback: () => {
          setShowDropdown(false)
          showExportChat()
        },
      },
    ])
  }, [isLogged, t, showExportChat])

  return (
    <div
      className="tw-relative tw-flex tw-h-[60px] tw-flex-shrink-0 tw-items-center tw-gap-2 tw-px-3"
      style={{ borderBottom: '1px solid var(--page-border)' }}
    >
      <button
        onClick={toggleMobileSidebar}
        className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-lg tw-transition-colors hover:tw-bg-[var(--page-surface-hover)] lg:tw-hidden"
      >
        <Icon icon={['far', 'bars']} className="tw-h-4 tw-w-4" style={{ color: 'var(--icon-default)' }} />
      </button>
      <button
        onClick={toggleCollapsed}
        className="tw-hidden tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-lg tw-transition-colors hover:tw-bg-[var(--page-surface-hover)] lg:tw-flex"
      >
        <Icon icon={['far', 'bars']} className="tw-h-4 tw-w-4" style={{ color: 'var(--icon-default)' }} />
      </button>

      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-2">
        <span className="tw-truncate tw-text-sm tw-font-semibold" style={{ color: 'var(--text-primary)' }}>
          {currentChat?.ticket?.title || t('chat')}
        </span>
        {isInOperatorMode && (
          <span
            className="tw-flex-shrink-0 tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--brand-text) 12%, transparent)',
              color: 'var(--brand-text)',
            }}
          >
            {t('operatorMode')}
          </span>
        )}
      </div>

      {dropdownMenu.length > 0 && (
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-lg tw-transition-colors hover:tw-bg-[var(--page-surface-hover)]"
        >
          <Icon
            icon={['far', 'ellipsis-vertical']}
            className="tw-h-4 tw-w-4"
            style={{ color: 'var(--icon-default)' }}
          />
        </button>
      )}

      {showDropdown && (
        <div
          ref={dropdownRef}
          className="animate-scale-in tw-absolute tw-right-3 tw-top-14 tw-z-50 tw-min-w-[180px] tw-overflow-hidden tw-rounded-xl tw-shadow-lg"
          style={{ backgroundColor: 'var(--surface-solid)', border: '1px solid var(--page-border)' }}
        >
          {dropdownMenu.map((item, idx) => (
            <div
              key={idx}
              onClick={item.callback}
              className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-px-3 tw-py-2.5 tw-text-sm tw-transition-colors hover:tw-bg-[var(--page-surface-hover)]"
              style={{ color: 'var(--text-primary)' }}
            >
              {item.icon}
              <span>{item.title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
